<template>
  <b-modal :id="modalId" centered scrollable hide-header-close size="lg"
           modal-class="menus-modal bulk-modal" @show="onShow" @hidden="onHidden">
    <template #modal-header="{ cancel }">
      <menu-modal-header icon="list-check" :title="title" :subtitle="subtitle" @close="cancel()"/>
    </template>

    <div class="bulk-details-bar">
      <div class="bulk-search">
        <b-icon icon="search"></b-icon>
        <b-form-input type="search" v-model="search" placeholder="جستجو در نام محصول یا شناسه..." @input="onSearch"/>
      </div>
      <span class="bulk-tag" v-if="meta">{{ formatNumber(meta.total) }} ردیف</span>
      <span class="bulk-tag is-warn" v-if="changedAfterOnPage">
        {{ formatNumber(changedAfterOnPage) }} مورد بعداً تغییر کرده (این صفحه)
      </span>
    </div>

    <div v-if="loading" class="bulk-state">
      <div class="spinner-border" role="status"></div>
      <p class="mb-0 mt-2">در حال بارگذاری...</p>
    </div>
    <div v-else-if="error" class="bulk-error">{{ error }}</div>
    <div v-else-if="!items.length" class="bulk-state">
      <b-icon icon="inbox"></b-icon>
      <p class="mb-0">{{ search ? 'موردی با این جستجو پیدا نشد.' : 'تغییری ثبت نشده است.' }}</p>
    </div>
    <div v-else>
      <div v-for="row in items" :key="row.id" class="bulk-detail-row"
           :class="{ 'is-variant': row.item_type === 'variant', 'is-changed-after': row.changed_after }">
        <div class="bulk-detail-thumb">
          <img v-if="row.image" :src="row.image" alt="" loading="lazy">
          <b-icon v-else :icon="row.item_type === 'variant' ? 'diagram-2' : 'box-seam'"></b-icon>
        </div>
        <div class="bulk-detail-body">
          <div class="bulk-detail-name">
            <span class="menus-id-chip">#{{ row.product_id }}</span>
            <strong>{{ row.product_name || 'محصول حذف‌شده' }}</strong>
            <span v-if="row.item_type === 'variant'" class="bulk-tag is-variant">
              {{ row.variant_label || ('تنوع #' + row.item_id) }}
            </span>
            <span v-if="row.product_deleted" class="bulk-tag is-deleted">در سطل زباله</span>
            <span v-if="row.changed_after" class="bulk-tag is-warn"
                  v-b-tooltip.hover title="مقدار فعلی با مقدار ثبت‌شده در این ویرایش گروهی متفاوت است">
              بعداً تغییر کرده
            </span>
          </div>
          <div class="bulk-diff">
            <div v-for="key in fieldKeys(row)" :key="row.id + key" class="bulk-diff-item is-changed">
              <span>{{ fieldLabel(key) }}</span>
              <bulk-value-change :old-text="display(key, row.old_values[key])" :new-text="display(key, row.new_values[key])"/>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #modal-footer="{ cancel }">
      <div class="menus-modal-actions">
        <template v-if="meta && meta.last_page > 1">
          <button type="button" class="menus-btn" :disabled="loading || page <= 1" @click="load(page - 1)">
            <b-icon icon="chevron-right"></b-icon>
            <span>قبلی</span>
          </button>
          <span class="align-self-center px-1">{{ formatNumber(page) }} / {{ formatNumber(meta.last_page) }}</span>
          <button type="button" class="menus-btn" :disabled="loading || page >= meta.last_page" @click="load(page + 1)">
            <span>بعدی</span>
            <b-icon icon="chevron-left"></b-icon>
          </button>
        </template>
        <button type="button" class="menus-btn is-primary" @click="cancel()">بستن</button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {formatNumber} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/Converter";
import MenuModalHeader from "../menu/MenuModalHeader.vue";
import BulkValueChange from "./BulkValueChange.vue";
import "../menu/menus-ui.css";
import "./bulk-edit-ui.css";

const LABELS = {
  price: 'قیمت',
  offer: 'تخفیف',
  quantity: 'تعداد',
  quantity_status: 'وضعیت موجودی',
  status: 'وضعیت انتشار',
}
const ORDER = ['price', 'offer', 'quantity', 'quantity_status', 'status']

export default {
  name: "ProductBulkEditDetails",
  components: {MenuModalHeader, BulkValueChange},
  props: {
    modalId: {
      type: String,
      default: 'product-bulk-edit-details'
    },
    bulkEdit: {
      default: null
    }
  },
  data() {
    return {
      items: [],
      meta: null,
      page: 1,
      search: '',
      loading: false,
      error: '',
      searchTimer: null,
      requestId: 0,
    }
  },
  computed: {
    title() {
      return this.bulkEdit ? `جزئیات ویرایش گروهی #${this.bulkEdit.id}` : 'جزئیات ویرایش گروهی'
    },
    subtitle() {
      if (!this.bulkEdit) return ''
      const parts = [`${formatNumber(this.bulkEdit.products_count || 0)} محصول`]
      if (this.bulkEdit.variants_count) parts.push(`${formatNumber(this.bulkEdit.variants_count)} تنوع`)
      return parts.join(' · ')
    },
    changedAfterOnPage() {
      return this.items.filter((row) => row.changed_after).length
    },
  },
  methods: {
    formatNumber,
    onShow() {
      this.search = ''
      this.items = []
      this.meta = null
      this.load(1)
    },
    onHidden() {
      clearTimeout(this.searchTimer)
    },
    onSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.load(1), 400)
    },
    async load(page = 1) {
      if (!this.bulkEdit) return
      const requestId = ++this.requestId
      this.loading = true
      this.error = ''
      try {
        const data = await this.$store.dispatch('ProductBulkEdit/fetchItems', {
          id: this.bulkEdit.id,
          page,
          search: this.search.trim(),
        })
        if (requestId !== this.requestId) return
        this.items = Array.isArray(data.data) ? data.data : []
        this.meta = data.meta || null
        this.page = (this.meta && this.meta.current_page) || page
      } catch (e) {
        if (requestId !== this.requestId) return
        const res = e && e.response && e.response.data
        this.error = (res && res.message) || 'خطا در دریافت جزئیات'
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    fieldKeys(row) {
      const keys = Object.keys(Object.assign({}, row.old_values || {}, row.new_values || {}))
      return ORDER.filter((key) => keys.indexOf(key) !== -1).concat(keys.filter((key) => ORDER.indexOf(key) === -1))
    },
    fieldLabel(key) {
      return LABELS[key] || key
    },
    display(key, value) {
      if (key === 'quantity_status') return Number(value) === 1 ? 'موجود' : 'ناموجود'
      if (key === 'status') return Number(value) === 1 ? 'منتشر' : 'پیش‌نویس'
      if (key === 'quantity') return value === null || value === undefined ? 'نامحدود' : formatNumber(value)
      if (value === null || value === undefined || value === '') return '—'
      return key === 'price' || key === 'offer' ? `${formatNumber(value)} تومان` : formatNumber(value)
    },
  },
}
</script>
